import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Inbox, Search } from 'lucide-react';
import {
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_VALUES,
  TICKET_STATUS_LABELS,
  TICKET_STATUS_VALUES,
  type TicketPriority,
  type TicketStatus,
  type TicketSummary,
} from '@electrodavisan/shared';
import { ROUTES } from '../../app/config';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Pagination,
  Select,
  SkeletonList,
  Table,
  TicketPriorityBadge,
  TicketStatusBadge,
  type Column,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { formatDateTime, formatRelative } from '../../lib/format';
import { useMyApplications } from '../../services/applications.service';
import { useSupportInboxCounts, useSupportTickets } from '../../services/tickets.service';

const PAGE_SIZE = 20;

/** Bandejas rapidas del panel de soporte. */
const INBOXES: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'Todos' },
  { key: 'unassigned', label: 'Sin asignar' },
  { key: 'mine', label: 'Mis tickets' },
  { key: 'open', label: 'Abiertos' },
  { key: 'in_progress', label: 'En progreso' },
  { key: 'waiting_user', label: 'Esperando usuario' },
  { key: 'resolved', label: 'Resueltos' },
  { key: 'closed', label: 'Cerrados' },
];

export function SupportTicketsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [inbox, setInbox] = useState(searchParams.get('inbox') ?? 'all');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');
  const [applicationId, setApplicationId] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // La bandeja viaja en la URL para poder enlazarla desde el dashboard.
  useEffect(() => {
    const fromUrl = searchParams.get('inbox');
    if (fromUrl && fromUrl !== inbox) {
      setInbox(fromUrl);
      setPage(1);
    }
    // Solo reacciona a cambios de la URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data: counts } = useSupportInboxCounts();
  const { data: applications } = useMyApplications();

  const { data, isLoading, isError, refetch } = useSupportTickets({
    page,
    pageSize: PAGE_SIZE,
    inbox,
    status: status ? ([status] as TicketStatus[]) : undefined,
    priority: priority ? ([priority] as TicketPriority[]) : undefined,
    applicationId: applicationId || undefined,
    search: search || undefined,
  });

  const selectInbox = (key: string): void => {
    setInbox(key);
    setPage(1);
    setSearchParams(key === 'all' ? {} : { inbox: key }, { replace: true });
  };

  const columns: Array<Column<TicketSummary>> = [
    {
      key: 'number',
      header: 'Ticket',
      render: (ticket) => (
        <span className="whitespace-nowrap font-mono text-[13px] text-text-primary">
          {ticket.ticketNumber}
        </span>
      ),
    },
    {
      key: 'subject',
      header: 'Asunto',
      render: (ticket) => (
        <div className="min-w-0 max-w-xs">
          <p className="truncate font-medium text-text-primary">{ticket.subject}</p>
          <p className="truncate text-[13px] text-text-muted">
            {ticket.applicationName ?? 'General'}
          </p>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'Usuario',
      hideBelow: 'lg',
      render: (ticket) => (
        <div className="min-w-0 max-w-[12rem]">
          <p className="truncate text-[13px] text-text-primary">
            {ticket.createdByName ?? '-'}
          </p>
          <p className="truncate text-[12px] text-text-muted">{ticket.createdByEmail ?? ''}</p>
        </div>
      ),
    },
    {
      key: 'priority',
      header: 'Prioridad',
      hideBelow: 'md',
      render: (ticket) => <TicketPriorityBadge priority={ticket.priority} />,
    },
    {
      key: 'status',
      header: 'Estado',
      render: (ticket) => <TicketStatusBadge status={ticket.status} />,
    },
    {
      key: 'agent',
      header: 'Agente',
      hideBelow: 'xl',
      render: (ticket) => (
        <span className="whitespace-nowrap text-[13px]">
          {ticket.assignedToName ?? 'Sin asignar'}
        </span>
      ),
    },
    {
      key: 'created',
      header: 'Creado',
      hideBelow: 'xl',
      render: (ticket) => (
        <span className="whitespace-nowrap text-[13px]">{formatDateTime(ticket.createdAt)}</span>
      ),
    },
    {
      key: 'updated',
      header: 'Actualizado',
      hideBelow: 'sm',
      render: (ticket) => (
        <span
          className="whitespace-nowrap text-[13px]"
          title={formatDateTime(ticket.lastActivityAt)}
        >
          {formatRelative(ticket.lastActivityAt)}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Mesa de ayuda"
        description="Todos los tickets del ecosistema."
      />

      {/* Bandejas con contadores. */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1" role="tablist" aria-label="Bandejas">
        {INBOXES.map((item) => (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={inbox === item.key}
            onClick={() => selectInbox(item.key)}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors',
              inbox === item.key
                ? 'border-primary/40 bg-primary-soft text-primary-bright'
                : 'border-border bg-surface text-text-secondary hover:bg-surface-hover',
            )}
          >
            {item.label}
            {counts?.[item.key] !== undefined ? (
              <span className="rounded-full bg-surface-secondary px-1.5 py-0.5 text-[11px] tabular-nums text-text-muted">
                {counts[item.key]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      <Card className="mb-4 p-4">
        <form
          className="grid gap-3 md:grid-cols-2 xl:grid-cols-5"
          onSubmit={(event) => {
            event.preventDefault();
            setSearch(searchInput.trim());
            setPage(1);
          }}
        >
          <Input
            placeholder="Numero, asunto o email"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            aria-label="Buscar"
            containerClassName="xl:col-span-2"
          />
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
            placeholder="Todos los estados"
            options={TICKET_STATUS_VALUES.map((value) => ({
              value,
              label: TICKET_STATUS_LABELS[value],
            }))}
            aria-label="Estado"
          />
          <Select
            value={priority}
            onChange={(event) => {
              setPriority(event.target.value);
              setPage(1);
            }}
            placeholder="Todas las prioridades"
            options={TICKET_PRIORITY_VALUES.map((value) => ({
              value,
              label: TICKET_PRIORITY_LABELS[value],
            }))}
            aria-label="Prioridad"
          />
          <div className="flex gap-2">
            <Select
              value={applicationId}
              onChange={(event) => {
                setApplicationId(event.target.value);
                setPage(1);
              }}
              placeholder="Todas las aplicaciones"
              options={(applications ?? []).map((application) => ({
                value: application.id,
                label: application.name,
              }))}
              aria-label="Aplicacion"
              containerClassName="flex-1"
            />
            <Button
              type="submit"
              variant="secondary"
              className="self-end"
              aria-label="Aplicar filtros"
            >
              <Search className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </form>
      </Card>

      {isError ? (
        <ErrorState onRetry={() => void refetch()} />
      ) : isLoading ? (
        <SkeletonList rows={6} />
      ) : (
        <>
          <Table
            columns={columns}
            rows={data?.items ?? []}
            rowKey={(ticket) => ticket.id}
            onRowClick={(ticket) => navigate(ROUTES.supportTicketDetail(ticket.id))}
            empty={
              <EmptyState
                icon={<Inbox className="h-6 w-6" aria-hidden />}
                title="No hay tickets en esta bandeja"
                description="Cuando los usuarios creen tickets apareceran aqui."
              />
            }
          />

          {data ? (
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              totalPages={data.totalPages}
              onPageChange={setPage}
            />
          ) : null}
        </>
      )}
    </>
  );
}
